export type BodyKey =
    | 'sun'
    | 'mercury'
    | 'venus'
    | 'earth'
    | 'mars'
    | 'jupiter'
    | 'saturn'
    | 'uranus'
    | 'neptune'
    | 'ceres'
    | 'pluto'
    | 'haumea'
    | 'makemake'
    | 'eris'

export type SolarBody = {
    key: BodyKey
    name: string
    kind: 'Star' | 'Planet' | 'Dwarf planet'
    colour: string
    texture: string | null
    radiusKm: number
    /** Display radius in the readable scale. */
    readableRadius: number
    diameter: string
    day: string
    year: string
    moons: string
    temperature: string
    summary: string
}

export const SOLAR_BODIES: SolarBody[] = [
    {
        key: 'sun',
        name: 'The Sun',
        kind: 'Star',
        colour: '#ffd27a',
        texture: '/textures/2k_sun.jpg',
        radiusKm: 695700,
        readableRadius: 3.2,
        diameter: '1,392,700 km',
        day: '25 to 35 days, faster at the equator',
        year: '230 million years around the galaxy',
        moons: '8 planets, 5 recognised dwarf planets',
        temperature: '5,500 °C at the surface',
        summary: 'An ordinary yellow dwarf, middle-aged at 4.6 billion years. It holds 99.86 per cent of all the mass in the solar system.'
    },
    {
        key: 'mercury',
        name: 'Mercury',
        kind: 'Planet',
        colour: '#b9aa9a',
        texture: '/textures/2k_mercury.jpg',
        radiusKm: 2439.7,
        readableRadius: 0.36,
        diameter: '4,879 km',
        day: '58.6 Earth days',
        year: '88 Earth days',
        moons: 'None',
        temperature: '−173 to 427 °C',
        summary: 'The smallest planet and the closest to the Sun. With almost no air to hold heat, its nights are colder than Antarctica and its days hot enough to melt lead.'
    },
    {
        key: 'venus',
        name: 'Venus',
        kind: 'Planet',
        colour: '#e9d4a8',
        texture: '/textures/2k_venus_atmosphere.jpg',
        radiusKm: 6051.8,
        readableRadius: 0.6,
        diameter: '12,104 km',
        day: '243 Earth days, spinning backwards',
        year: '225 Earth days',
        moons: 'None',
        temperature: '464 °C average',
        summary: 'Earth’s near twin in size, smothered in clouds of sulphuric acid over an atmosphere ninety times thicker than ours. The hottest planet, even though Mercury is closer to the Sun.'
    },
    {
        key: 'earth',
        name: 'Earth',
        kind: 'Planet',
        colour: '#6f9bd1',
        texture: '/textures/2k_earth_daymap.jpg',
        radiusKm: 6371,
        readableRadius: 0.62,
        diameter: '12,742 km',
        day: '23 h 56 min',
        year: '365.25 days',
        moons: '1',
        temperature: '15 °C average',
        summary: 'You are here. The only world we know of with oceans of liquid water on its surface, and with anyone looking back.'
    },
    {
        key: 'mars',
        name: 'Mars',
        kind: 'Planet',
        colour: '#d4744a',
        texture: '/textures/2k_mars.jpg',
        radiusKm: 3389.5,
        readableRadius: 0.45,
        diameter: '6,779 km',
        day: '24 h 37 min',
        year: '687 Earth days',
        moons: '2',
        temperature: '−65 °C average',
        summary: 'A cold desert with the tallest volcano and the longest canyon in the solar system. Rivers once ran here; today the water is locked in ice at the poles and underground.'
    },
    {
        key: 'jupiter',
        name: 'Jupiter',
        kind: 'Planet',
        colour: '#d9b98e',
        texture: '/textures/2k_jupiter.jpg',
        radiusKm: 69911,
        readableRadius: 1.6,
        diameter: '139,820 km',
        day: '9 h 56 min',
        year: '11.9 Earth years',
        moons: '97 known',
        temperature: '−110 °C at the cloud tops',
        summary: 'More than twice the mass of all the other planets put together. Its Great Red Spot is a storm wider than Earth that has been raging for at least 190 years.'
    },
    {
        key: 'saturn',
        name: 'Saturn',
        kind: 'Planet',
        colour: '#e3cf9b',
        texture: '/textures/2k_saturn.jpg',
        radiusKm: 58232,
        readableRadius: 1.35,
        diameter: '116,460 km',
        day: '10 h 34 min',
        year: '29.4 Earth years',
        moons: '274 known',
        temperature: '−140 °C at the cloud tops',
        summary: 'Light enough to float in a large enough bathtub. Its rings are mostly water ice, 270,000 km across and in places only ten metres thick.'
    },
    {
        key: 'uranus',
        name: 'Uranus',
        kind: 'Planet',
        colour: '#a9dce3',
        texture: '/textures/2k_uranus.jpg',
        radiusKm: 25362,
        readableRadius: 1.0,
        diameter: '50,724 km',
        day: '17 h 14 min, spinning backwards',
        year: '84 Earth years',
        moons: '29 known',
        temperature: '−195 °C at the cloud tops',
        summary: 'Tipped over on its side, so each pole spends 42 years in sunlight and then 42 in darkness. The first planet discovered with a telescope, by William Herschel in Bath in 1781.'
    },
    {
        key: 'neptune',
        name: 'Neptune',
        kind: 'Planet',
        colour: '#6f8ff0',
        texture: '/textures/2k_neptune.jpg',
        radiusKm: 24622,
        readableRadius: 0.98,
        diameter: '49,244 km',
        day: '16 h 6 min',
        year: '165 Earth years',
        moons: '16 known',
        temperature: '−200 °C at the cloud tops',
        summary: 'Found in 1846 by mathematics before anyone saw it, from the way it tugged on Uranus. Its winds are the fastest in the solar system, over 2,000 km an hour.'
    },
    {
        key: 'ceres',
        name: 'Ceres',
        kind: 'Dwarf planet',
        colour: '#9c968b',
        texture: null,
        radiusKm: 469.7,
        readableRadius: 0.2,
        diameter: '939 km',
        day: '9 h 4 min',
        year: '4.6 Earth years',
        moons: 'None',
        temperature: '−105 °C average',
        summary: 'The largest object in the asteroid belt, with bright patches of salt left behind where briny water reached the surface.'
    },
    {
        key: 'pluto',
        name: 'Pluto',
        kind: 'Dwarf planet',
        colour: '#cdb59a',
        texture: null,
        radiusKm: 1188.3,
        readableRadius: 0.24,
        diameter: '2,377 km',
        day: '6.4 Earth days, spinning backwards',
        year: '248 Earth years',
        moons: '5',
        temperature: '−229 °C average',
        summary: 'A planet until 2006. New Horizons found a heart-shaped glacier of frozen nitrogen and mountains of water ice as hard as rock.'
    },
    {
        key: 'haumea',
        name: 'Haumea',
        kind: 'Dwarf planet',
        colour: '#c9c4bd',
        texture: null,
        radiusKm: 816,
        readableRadius: 0.2,
        diameter: 'about 1,630 km, egg-shaped',
        day: '3 h 55 min',
        year: '284 Earth years',
        moons: '2',
        temperature: '−241 °C average',
        summary: 'Spinning so fast that it has stretched into an egg shape, and the only dwarf planet known to have a ring.'
    },
    {
        key: 'makemake',
        name: 'Makemake',
        kind: 'Dwarf planet',
        colour: '#c99a7e',
        texture: null,
        radiusKm: 715,
        readableRadius: 0.2,
        diameter: '1,430 km',
        day: '22 h 50 min',
        year: '306 Earth years',
        moons: '1',
        temperature: '−239 °C average',
        summary: 'A reddish world of frozen methane out beyond Pluto, discovered just after Easter in 2005 and named for a creator god of Easter Island.'
    },
    {
        key: 'eris',
        name: 'Eris',
        kind: 'Dwarf planet',
        colour: '#dcd8d2',
        texture: null,
        radiusKm: 1163,
        readableRadius: 0.24,
        diameter: '2,326 km',
        day: '15.8 Earth days',
        year: '559 Earth years',
        moons: '1',
        temperature: '−231 °C average',
        summary: 'Nearly the same size as Pluto and more massive. Its discovery in 2005 is what forced astronomers to decide what a planet is.'
    }
]

export const bodyByKey = (key: string): SolarBody | undefined => SOLAR_BODIES.find(body => body.key === key)

/** Approximate orbits for dwarf planets not covered by the ephemeris, in J2000 ecliptic degrees. */
export const DWARF_ELEMENTS: Record<'ceres' | 'haumea' | 'makemake' | 'eris', { a: number, e: number, i: number, node: number, peri: number, mean: number, epoch: string }> = {
    ceres: { a: 2.767, e: 0.0785, i: 10.59, node: 80.3, peri: 73.6, mean: 300, epoch: '2026-10-13' },
    haumea: { a: 43.2, e: 0.191, i: 28.2, node: 122.2, peri: 239, mean: 212, epoch: '2026-10-13' },
    makemake: { a: 45.4, e: 0.161, i: 29, node: 79.4, peri: 297.2, mean: 168, epoch: '2026-10-13' },
    eris: { a: 67.86, e: 0.4361, i: 44.04, node: 35.95, peri: 151.6, mean: 206, epoch: '2026-10-13' }
}

export type MoonFact = {
    name: string
    parent: BodyKey
    diameter: string
    orbit: string
    note: string
}

export const MOONS: MoonFact[] = [
    { name: 'The Moon', parent: 'earth', diameter: '3,475 km', orbit: '27.3 days', note: 'Slowly drifting away from us, about 3.8 cm a year.' },
    { name: 'Phobos', parent: 'mars', diameter: '22 km', orbit: '7.7 hours', note: 'Orbits faster than Mars turns, so it rises in the west.' },
    { name: 'Deimos', parent: 'mars', diameter: '12 km', orbit: '30.3 hours', note: 'Seen from the surface of Mars it would look like a bright star.' },
    { name: 'Io', parent: 'jupiter', diameter: '3,643 km', orbit: '1.8 days', note: 'The most volcanic place in the solar system.' },
    { name: 'Europa', parent: 'jupiter', diameter: '3,122 km', orbit: '3.6 days', note: 'An ice shell over an ocean with twice the water of Earth’s seas.' },
    { name: 'Ganymede', parent: 'jupiter', diameter: '5,268 km', orbit: '7.2 days', note: 'The largest moon of all, bigger than Mercury.' },
    { name: 'Callisto', parent: 'jupiter', diameter: '4,821 km', orbit: '16.7 days', note: 'The most heavily cratered surface we know.' },
    { name: 'Mimas', parent: 'saturn', diameter: '396 km', orbit: '0.9 days', note: 'One giant crater makes it look like the Death Star.' },
    { name: 'Enceladus', parent: 'saturn', diameter: '504 km', orbit: '1.4 days', note: 'Fountains of water ice spray from cracks at its south pole.' },
    { name: 'Tethys', parent: 'saturn', diameter: '1,062 km', orbit: '1.9 days', note: 'Almost pure water ice.' },
    { name: 'Dione', parent: 'saturn', diameter: '1,123 km', orbit: '2.7 days', note: 'Streaked with bright cliffs of ice.' },
    { name: 'Rhea', parent: 'saturn', diameter: '1,527 km', orbit: '4.5 days', note: 'Saturn’s second largest moon.' },
    { name: 'Titan', parent: 'saturn', diameter: '5,149 km', orbit: '15.9 days', note: 'Thick orange air, and lakes of liquid methane.' },
    { name: 'Iapetus', parent: 'saturn', diameter: '1,469 km', orbit: '79.3 days', note: 'One side as dark as coal, the other as bright as snow.' },
    { name: 'Miranda', parent: 'uranus', diameter: '472 km', orbit: '1.4 days', note: 'Has a cliff about twenty kilometres high.' },
    { name: 'Ariel', parent: 'uranus', diameter: '1,158 km', orbit: '2.5 days', note: 'The brightest of Uranus’s moons.' },
    { name: 'Umbriel', parent: 'uranus', diameter: '1,169 km', orbit: '4.1 days', note: 'The darkest of Uranus’s large moons.' },
    { name: 'Titania', parent: 'uranus', diameter: '1,577 km', orbit: '8.7 days', note: 'Named, like most of its sisters, for a character from Shakespeare.' },
    { name: 'Oberon', parent: 'uranus', diameter: '1,523 km', orbit: '13.5 days', note: 'The outermost of the five large moons.' },
    { name: 'Triton', parent: 'neptune', diameter: '2,707 km', orbit: '5.9 days, backwards', note: 'Probably a captured world from the Kuiper belt, with geysers of nitrogen.' },
    { name: 'Charon', parent: 'pluto', diameter: '1,212 km', orbit: '6.4 days', note: 'So large that Pluto and Charon orbit a point in the space between them.' }
]

export type Mission = {
    key: string
    name: string
    colour: string
    launch: string
    flybys: { body: 'earth' | 'jupiter' | 'saturn' | 'uranus' | 'neptune' | 'pluto', date: string }[]
    /** Asymptotic heading in J2000 right ascension (hours) and declination (degrees). */
    heading: { ra: number, dec: number }
    /** Cruise speed after the last encounter, in AU per year. */
    speed: number
    status: string
}

export const MISSIONS: Mission[] = [
    {
        key: 'voyager-1',
        name: 'Voyager 1',
        colour: '#e7d3a1',
        launch: '1977-09-05',
        flybys: [
            { body: 'earth', date: '1977-09-05' },
            { body: 'jupiter', date: '1979-03-05' },
            { body: 'saturn', date: '1980-11-12' }
        ],
        heading: { ra: 17.2, dec: 12.1 },
        speed: 3.57,
        status: 'In interstellar space since 2012, still sending data.'
    },
    {
        key: 'voyager-2',
        name: 'Voyager 2',
        colour: '#a9c6e8',
        launch: '1977-08-20',
        flybys: [
            { body: 'earth', date: '1977-08-20' },
            { body: 'jupiter', date: '1979-07-09' },
            { body: 'saturn', date: '1981-08-26' },
            { body: 'uranus', date: '1986-01-24' },
            { body: 'neptune', date: '1989-08-25' }
        ],
        heading: { ra: 20.1, dec: -57.8 },
        speed: 3.06,
        status: 'The only spacecraft to visit Uranus and Neptune.'
    },
    {
        key: 'new-horizons',
        name: 'New Horizons',
        colour: '#b8e0c4',
        launch: '2006-01-19',
        flybys: [
            { body: 'earth', date: '2006-01-19' },
            { body: 'jupiter', date: '2007-02-28' },
            { body: 'pluto', date: '2015-07-14' }
        ],
        heading: { ra: 19.1, dec: -20.6 },
        speed: 2.7,
        status: 'Flew past Pluto in 2015 and Arrokoth in 2019.'
    },
    {
        key: 'pioneer-10',
        name: 'Pioneer 10',
        colour: '#d9a98f',
        launch: '1972-03-03',
        flybys: [
            { body: 'earth', date: '1972-03-03' },
            { body: 'jupiter', date: '1973-12-04' }
        ],
        heading: { ra: 5.1, dec: 25.9 },
        speed: 2.54,
        status: 'Silent since 2003, coasting toward Aldebaran.'
    },
    {
        key: 'pioneer-11',
        name: 'Pioneer 11',
        colour: '#c8b8e8',
        launch: '1973-04-06',
        flybys: [
            { body: 'earth', date: '1973-04-06' },
            { body: 'jupiter', date: '1974-12-03' },
            { body: 'saturn', date: '1979-09-01' }
        ],
        heading: { ra: 18.8, dec: -8.6 },
        speed: 2.37,
        status: 'Silent since 1995, heading toward the constellation of Aquila.'
    }
]
